import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid, PieChart, Pie, Cell, RadialBarChart, RadialBar, LineChart, Line } from "recharts";
import { DownloadIcon, FileSpreadsheetIcon, PresentationIcon, TrendingUpIcon, TruckIcon, WrenchIcon, DropletsIcon, RadioTowerIcon, ZapIcon, SlidersHorizontalIcon, HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useActiveBase } from "@/context/BaseContext";

const PALETTE = ["#00E5FF", "#7B61FF", "#FFD60A", "#34C759", "#FF9F0A", "#FF3B30", "#FF62B0", "#5AC8FA"];

function Sparkles() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="grad" cx="50%" cy="0%" r="80%">
          <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.15" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)" />
    </svg>
  );
}

function BigKpi({ label, value, unit, accent, testId, icon: Icon }) {
  return (
    <div className="relative flex-1 min-w-[180px] px-6 py-4 border-r border-[#1e2b4d] last:border-r-0" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="text-[9px] uppercase tracking-[0.3em] text-slate-400">{label}</span>
      </div>
      <div className="font-display text-4xl md:text-5xl font-black leading-none" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{unit}</div>
    </div>
  );
}

function GaugeCard({ label, value, target, color }) {
  const pct = Math.min(100, Math.round((value / target) * 100));
  const data = [{ name: label, value: pct, fill: color }];
  return (
    <div className="bg-[#0d1a35] border border-[#1e2b4d] p-4 relative overflow-hidden rounded-lg">
      <div className="text-[9px] uppercase tracking-[0.3em] text-slate-400 mb-2">{label}</div>
      <ResponsiveContainer width="100%" height={140}>
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={210} endAngle={-30}>
          <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "#1a2648" }} fill={color} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center mt-2 pointer-events-none">
        <div className="font-display font-black text-3xl" style={{ color }}>{pct}%</div>
        <div className="text-[9px] text-slate-400 uppercase">meta</div>
      </div>
      <div className="text-[10px] font-mono text-slate-400 text-center mt-1">{value.toLocaleString("pt-BR")} / {target.toLocaleString("pt-BR")}</div>
    </div>
  );
}

const UF_MAP_POS = {
  AC: { x: 12, y: 34 }, AM: { x: 22, y: 30 }, RR: { x: 28, y: 12 }, PA: { x: 42, y: 26 }, AP: { x: 47, y: 14 }, TO: { x: 46, y: 42 },
  RO: { x: 22, y: 42 }, MT: { x: 36, y: 48 }, MS: { x: 40, y: 62 }, GO: { x: 48, y: 52 }, DF: { x: 51, y: 52 }, BA: { x: 62, y: 44 },
  MA: { x: 54, y: 30 }, PI: { x: 60, y: 34 }, CE: { x: 68, y: 30 }, RN: { x: 74, y: 30 }, PB: { x: 76, y: 34 }, PE: { x: 74, y: 38 },
  AL: { x: 76, y: 42 }, SE: { x: 74, y: 44 }, ES: { x: 66, y: 62 }, RJ: { x: 60, y: 66 }, MG: { x: 56, y: 58 }, SP: { x: 52, y: 66 },
  PR: { x: 48, y: 72 }, SC: { x: 50, y: 78 }, RS: { x: 46, y: 84 },
};

function BrasilMap({ heatmap }) {
  const max = Math.max(1, ...Object.values(heatmap || {}));
  return (
    <svg viewBox="0 0 100 100" className="w-full h-[300px]">
      <defs>
        <radialGradient id="mapg" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#7B61FF" stopOpacity="0.15" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="url(#mapg)" />
      {Object.entries(UF_MAP_POS).map(([uf, pos]) => {
        const val = heatmap?.[uf] || 0;
        const size = 1.5 + (val / max) * 4;
        const opacity = 0.25 + (val / max) * 0.75;
        return (
          <g key={uf}>
            <circle cx={pos.x} cy={pos.y} r={size} fill="#00E5FF" opacity={opacity}>
              {val > 0 && <animate attributeName="r" values={`${size};${size + 0.8};${size}`} dur="2s" repeatCount="indefinite" />}
            </circle>
            <text x={pos.x} y={pos.y + 0.5} textAnchor="middle" fontSize="1.6" fill="#e2e8f0" opacity="0.9" fontFamily="monospace">{uf}</text>
            {val > 0 && <text x={pos.x} y={pos.y + 4.2} textAnchor="middle" fontSize="1.8" fill="#FFD60A" fontWeight="bold">{val}</text>}
          </g>
        );
      })}
    </svg>
  );
}

export default function RelatoriosBI() {
  const { activeBase, setActiveBase, clearActiveBase } = useActiveBase();
  const [bases, setBases] = useState([]);
  const [mensal, setMensal] = useState([]);
  const [chegadas, setChegadas] = useState([]);
  const [manut, setManut] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [containers, setContainers] = useState([]);
  const [ano, setAno] = useState("2026");
  const [produto, setProduto] = useState("Todos");
  const [meta, setMeta] = useState(50000); // Litros/mês target
  const [cenario, setCenario] = useState(0);

  const load = async () => {
    const [m, c, mn, r, cont, bs] = await Promise.all([
      api.get("/relatorios/mensal"),
      api.get("/relatorios/chegadas"),
      api.get("/relatorios/manutencao"),
      api.get("/relatorios/produtos"),
      api.get("/containers").catch(() => ({ data: [] })),
      api.get("/bases").catch(() => ({ data: [] })),
    ]);
    setMensal(m.data);
    setChegadas(c.data);
    setManut(mn.data);
    setRanking(r.data);
    setContainers(c.data.length ? cont.data : cont.data);
    setBases(bs.data.filter((b) => b.active !== false));
  };
  useEffect(() => { load(); }, [activeBase?.code]);

  const download = async (kind) => {
    try {
      const res = await api.get(`/relatorios/export/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chemwms_${kind === "xlsx" ? "relatorio" : "apresentacao"}_${new Date().toISOString().slice(0,10)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Download iniciado: .${kind}`);
    } catch (e) { toast.error("Falha no download"); }
  };

  const filteredMensal = useMemo(() => mensal.filter((m) => !ano || ano === "Todos" || m.mes.startsWith(ano)), [mensal, ano]);
  const totalLitros = filteredMensal.reduce((a, b) => a + b.litros, 0);
  const totalContentores = filteredMensal.reduce((a, b) => a + b.contentores, 0);
  const totalChegadas = chegadas.reduce((a, b) => a + b.chegadas, 0);
  const totalReparos = manut.reduce((a, b) => a + b.reparos, 0);
  const totalReprov = manut.reduce((a, b) => a + b.reprovacoes, 0);

  const cenarioLitros = Math.round(totalLitros * (1 + cenario / 100));
  const avgMonthly = filteredMensal.length ? totalLitros / filteredMensal.length : 0;
  const metaAtingida = avgMonthly;

  const produtosPie = ranking.slice(0, 5).map((r, i) => ({ name: r.produto?.slice(0, 22) || "?", value: r.litros, fill: PALETTE[i % PALETTE.length] }));

  // Heatmap por UF (dos containers em NO_CLIENTE + cliente destino)
  const heatmap = useMemo(() => {
    const map = {};
    containers.forEach((c) => {
      // best guess do UF: procurar sufixo /XX em cliente ou usar SP como default
      const m = /\/([A-Z]{2})/.exec(c.cliente || "");
      const uf = m ? m[1] : null;
      if (uf) map[uf] = (map[uf] || 0) + 1;
    });
    // se vazio, coloca alguns valores demo baseados em quantidade envasada por lote
    if (!Object.keys(map).length) {
      ["SP", "RJ", "MG", "BA", "RS", "PR"].forEach((uf, i) => { map[uf] = totalContentores > 0 ? Math.floor(totalContentores / (i + 2)) : 0; });
    }
    return map;
  }, [containers, totalContentores]);

  const anos = ["Todos", "2026", "2025", "2024"];
  const produtos = useMemo(() => ["Todos", ...new Set(ranking.map((r) => r.produto).filter(Boolean))], [ranking]);

  return (
    <div className="min-h-screen relative" style={{
      background: "linear-gradient(135deg, #050b1e 0%, #0a1533 40%, #0e1b3f 100%)",
    }}>
      <Sparkles />

      {/* Top bar */}
      <div className="relative px-6 py-4 border-b border-[#1e2b4d] flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF] to-[#7B61FF] flex items-center justify-center rounded" >
            <ZapIcon className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.4em] text-[#00E5FF]">Business Intelligence · Real Time</div>
            <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-white">RELATÓRIO OPERACIONAL</div>
            {activeBase && (
              <div className="text-[10px] uppercase tracking-widest text-[#FFD60A] mt-1" data-testid="bi-active-base">
                ▸ Escopo ativo: <span className="font-bold">{activeBase.name}</span>
              </div>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/dashboard" className="text-slate-300 hover:text-white flex items-center gap-1 text-xs uppercase tracking-widest">
            <HomeIcon className="w-3 h-3" /> Home
          </Link>
          <button onClick={() => download("xlsx")} data-testid="bi-export-xlsx" className="bg-[#34C759] text-black h-10 px-4 font-bold uppercase tracking-wider text-[10px] flex items-center gap-2 hover:brightness-110">
            <FileSpreadsheetIcon className="w-3 h-3" /> Excel
          </button>
          <button onClick={() => download("pptx")} data-testid="bi-export-pptx" className="bg-[#FFD60A] text-black h-10 px-4 font-bold uppercase tracking-wider text-[10px] flex items-center gap-2 hover:brightness-110">
            <PresentationIcon className="w-3 h-3" /> PowerPoint
          </button>
        </div>
      </div>

      {/* KPI band */}
      <div className="relative bg-[#0d1a35]/60 backdrop-blur border-b border-[#1e2b4d] flex flex-wrap divide-x divide-[#1e2b4d]">
        <BigKpi testId="bi-tile-litros" label="Litros Envasados" value={(totalLitros / 1000).toFixed(1) + "K"} unit={`12 meses · média ${(avgMonthly/1000).toFixed(1)}K/mês`} accent="#00E5FF" icon={DropletsIcon} />
        <BigKpi testId="bi-tile-contentores" label="Contentores Envasados" value={totalContentores.toLocaleString("pt-BR")} unit="unidades processadas" accent="#7B61FF" icon={TrendingUpIcon} />
        <BigKpi testId="bi-tile-chegadas" label="Chegadas Granel" value={totalChegadas} unit="cargas recebidas" accent="#FFD60A" icon={TruckIcon} />
        <BigKpi testId="bi-tile-reparos" label="Reparos" value={totalReparos} unit="ordens concluídas" accent="#34C759" icon={WrenchIcon} />
        <BigKpi testId="bi-tile-reprov" label="Reprovações" value={totalReprov} unit="inspeções negativas" accent="#FF3B30" icon={RadioTowerIcon} />
      </div>

      <div className="relative grid grid-cols-12 gap-4 p-4">
        {/* Sidebar Filtros */}
        <div className="col-span-12 lg:col-span-2 space-y-3">
          <div className="bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
            <div className="flex items-center gap-2 mb-3">
              <SlidersHorizontalIcon className="w-4 h-4 text-[#00E5FF]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Filtros</div>
            </div>
            <label className="block text-[9px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Base Operacional</label>
            <select
              value={activeBase?.code || ""}
              onChange={(e) => {
                if (!e.target.value) { clearActiveBase(); return; }
                const b = bases.find((x) => x.code === e.target.value);
                if (b) setActiveBase({ code: b.code, name: b.name });
              }}
              data-testid="bi-filter-base"
              className="w-full h-9 bg-[#050b1e] border border-[#1e2b4d] text-slate-200 text-xs font-mono px-2"
            >
              <option value="">Todas as bases (consolidado)</option>
              {bases.map((b) => <option key={b.code} value={b.code}>{b.name} · {b.code}</option>)}
            </select>
            <label className="block text-[9px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Ano</label>
            <select value={ano} onChange={(e) => setAno(e.target.value)} data-testid="bi-filter-ano" className="w-full h-9 bg-[#050b1e] border border-[#1e2b4d] text-slate-200 text-xs font-mono px-2">
              {anos.map((a) => <option key={a}>{a}</option>)}
            </select>
            <label className="block text-[9px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Produto</label>
            <select value={produto} onChange={(e) => setProduto(e.target.value)} data-testid="bi-filter-produto" className="w-full h-9 bg-[#050b1e] border border-[#1e2b4d] text-slate-200 text-xs font-mono px-2">
              {produtos.map((p) => <option key={p} value={p}>{(p || "").slice(0, 30)}</option>)}
            </select>
            <label className="block text-[9px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Meta mensal (L)</label>
            <input type="number" value={meta} onChange={(e) => setMeta(parseFloat(e.target.value) || 0)} className="w-full h-9 bg-[#050b1e] border border-[#1e2b4d] text-slate-200 text-xs font-mono px-2" />
          </div>

          {/* E se... */}
          <div className="bg-gradient-to-br from-[#0d1a35] to-[#1a1740] border border-[#7B61FF]/40 p-4 rounded">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B61FF] mb-2">Simulador · E se?</div>
            <div className="text-white font-display text-lg leading-tight mb-2">Aumentar produção em</div>
            <div className="font-display font-black text-5xl text-[#00E5FF]">{cenario}%</div>
            <input type="range" min="-30" max="100" step="5" value={cenario} onChange={(e) => setCenario(parseInt(e.target.value))} className="w-full accent-[#00E5FF] mt-2" />
            <div className="text-[10px] text-slate-400 mt-2">Cenário:</div>
            <div className="font-mono text-[#FFD60A] font-bold">{(cenarioLitros/1000).toFixed(1)}K L</div>
          </div>
        </div>

        {/* Main area */}
        <div className="col-span-12 lg:col-span-10 space-y-4">
          {/* Row 1: Gauges + Line */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-3">
              <GaugeCard label="Envase vs Meta" value={Math.round(metaAtingida)} target={meta} color="#FFD60A" />
            </div>
            <div className="col-span-12 md:col-span-3">
              <GaugeCard label="Cenário vs Meta" value={Math.round(metaAtingida * (1 + cenario / 100))} target={meta} color="#00E5FF" />
            </div>
            <div className="col-span-12 md:col-span-6 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Envase Mensal (Litros)</div>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={filteredMensal}>
                  <defs>
                    <linearGradient id="colLitros" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="#00E5FF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="mes" stroke="#64748b" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2b4d" />
                  <Tooltip contentStyle={{ backgroundColor: "#050b1e", border: "1px solid #1e2b4d", borderRadius: 4 }} />
                  <Area type="monotone" dataKey="litros" stroke="#00E5FF" strokeWidth={2} fill="url(#colLitros)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 2: Chart + Map + Donut */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-4 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Chegadas × Manutenção</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chegadas.map((c, i) => ({ mes: c.mes, chegadas: c.chegadas, reparos: manut[i]?.reparos || 0 }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2b4d" />
                  <XAxis dataKey="mes" stroke="#64748b" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 9 }} />
                  <Tooltip contentStyle={{ backgroundColor: "#050b1e", border: "1px solid #1e2b4d" }} />
                  <Line type="monotone" dataKey="chegadas" stroke="#FFD60A" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="reparos" stroke="#7B61FF" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="col-span-12 md:col-span-4 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-1">Distribuição Nacional</div>
              <div className="text-[9px] text-slate-500 mb-2">Contentores expedidos por UF</div>
              <BrasilMap heatmap={heatmap} />
            </div>

            <div className="col-span-12 md:col-span-4 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Top 5 Produtos</div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={produtosPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {produtosPie.map((_, i) => <Cell key={i} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#050b1e", border: "1px solid #1e2b4d" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {produtosPie.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2 text-[10px]">
                    <span className="w-2 h-2" style={{ backgroundColor: p.fill }}></span>
                    <span className="text-slate-300 flex-1 truncate">{p.name}</span>
                    <span className="font-mono text-white">{Math.round(p.value).toLocaleString("pt-BR")}L</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Ranking table + bar */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-7 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Ranking Top 10 · Litros por Produto</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-[#1e2b4d]">
                    <th className="text-left py-2 w-8">#</th>
                    <th className="text-left py-2">Produto</th>
                    <th className="text-right py-2">Litros</th>
                    <th className="text-right py-2">Cont.</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.length === 0 && <tr><td colSpan="4" className="text-center py-6 text-slate-500">Sem envase registrado ainda.</td></tr>}
                  {ranking.slice(0, 10).map((r, i) => (
                    <tr key={r.produto} className="border-b border-[#1e2b4d]/50 hover:bg-[#1e2b4d]/30" data-testid={`bi-rank-${i+1}`}>
                      <td className="py-2 font-mono font-bold" style={{ color: PALETTE[i % PALETTE.length] }}>#{i + 1}</td>
                      <td className="py-2 text-slate-200">{r.produto}</td>
                      <td className="py-2 text-right font-mono text-white">{r.litros.toLocaleString("pt-BR")}</td>
                      <td className="py-2 text-right font-mono text-slate-400">{r.contentores}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="col-span-12 md:col-span-5 bg-[#0d1a35] border border-[#1e2b4d] p-4 rounded">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Chegadas de Granel × Litros</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chegadas} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2b4d" />
                  <XAxis type="number" stroke="#64748b" tick={{ fontSize: 9 }} />
                  <YAxis type="category" dataKey="mes" stroke="#64748b" tick={{ fontSize: 9 }} width={55} />
                  <Tooltip contentStyle={{ backgroundColor: "#050b1e", border: "1px solid #1e2b4d" }} />
                  <Bar dataKey="volume_recebido" fill="#7B61FF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-[#1e2b4d] px-6 py-3 flex items-center justify-between text-[9px] uppercase tracking-widest text-slate-500">
        <div className="flex items-center gap-2">
          <DownloadIcon className="w-3 h-3" /> Consolidação: envase mensal · chegadas · manutenção · ranking · estoque atual.
        </div>
        <div>Atualizado: {new Date().toLocaleString("pt-BR")}</div>
      </div>
    </div>
  );
}
